import * as React from 'react';
import { useObjectVal } from 'react-firebase-hooks/database';
import { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Alert,
  Input,
} from 'reactstrap';
import { Field } from '@availity/form';
import { SelectField } from '@availity/select';
import '@availity/yup';
import { ref } from '../../../utils/firebase';
import { Loader } from '../../../components/Loader';
import { Can } from '../../../auth/Can';
import { logger } from '../../../logging';
import { useFormikContext } from 'formik';
import {
  BrandInputField,
  dataTypes,
} from '../../../components/brand/BrandInputField';
import { BrandService } from '../../../components/brand/BrandLookup';
import { equalTo, get, orderByChild, query } from 'firebase/database';

const accountStyles = require('../account.css');

// account name should be unique - maybe this should be onsubmit tho (as this is reused for view/edit)
// we should debounce this/throttle it
export const validateAttentiveAccountName = async (
  account_name,
  id,
  setFieldError,
  setHasFormErrors
) => {
  logger.debug(
    `[AttentiveFields] validateAttentiveAccountName - account_name: ${account_name}, id: ${id}, typeof id: ${typeof id}`
  );
  const errorMessage = 'An account name must be provided and unique';

  const value = account_name ? account_name.trim() : '';

  if (!value || value === '') {
    logger.debug(
      '[AttentiveFields] validateAttentiveAccountName - there is no value, so we dont lookup. Returning as this is the cause of a setstate error'
    );
    setFieldError('name', errorMessage);
    return false; // there's no value so it is invalid
  }

  const accountsRef = ref('accounts/attentive');
  const accountsQuery = query(
    accountsRef,
    orderByChild('account_name'),
    equalTo(value)
  );
  const accountsSnap = await get(accountsQuery);

  const accounts = [];
  accountsSnap.forEach((v) => {
    accounts.push({ ...v.val(), id: v.key });
  });

  // so obviously there's an account with this name (if we're editing)
  if (accounts.length > 0) {
    const account = accounts[0];
    // if there is an account with this name but a different uid, then we have a validation error
    if (account.id !== id) {
      logger.debug(
        `[AttentiveFields] validateAttentiveAccountName - account.id (${account.id}) and id (${id}) are not the same`
      );
      setFieldError('account_name', errorMessage);
      return false;
    }
  }

  return true;
};

export const AttentiveFields = ({
  model,
  setCanSave,
  system,
  account_id,
  errors,
  decommissioningCommenced,
  decommissioningStatus,
}) => {
  const formik = useFormikContext();
  const [modal, setModal] = useState(false);
  const [activeErrorMessage, setActiveErrorMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [enumerations, enumerationsLoading] = useObjectVal(
    ref('enumerations') as any
  ) as any;
  const [countries, countriesLoading] = useObjectVal(
    ref('countries') as any
  ) as any;
  const [labelValues, labelLoading, labelError] = useObjectVal(
    ref('labels') as any
  );

  const checkIfCanSave = () => {
    if (errorMessage === '') {
      setCanSave(true);
    } else {
      setCanSave(false);
    }
  };

  useEffect(() => {
    if (errors) {
      const invalidFields = Object.keys(errors);
      logger.debug(`invalidFields: ${JSON.stringify(invalidFields)}`);
      if (invalidFields.length > 0) {
        setErrorMessage(
          `The following fields are set incorrectly: ${JSON.stringify(
            invalidFields
          )}`
        );
        checkIfCanSave();
      } else {
        setErrorMessage('');
        checkIfCanSave();
      }
    }
  });

  if (
    labelLoading ||
    !enumerations ||
    enumerationsLoading ||
    countriesLoading ||
    !countries
  ) {
    return <Loader loading={true} />;
  }

  const handleNameChange = (e) => {
    logger.debug(
      `[AttentiveFields] handleNameChanged: e.target.value: ${e.target.value}`
    );
    const nameClean = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
    formik.setFieldValue('account_name', nameClean);
  };

  const handleCanopusIDChange = (brandId, brandName) => {
    logger.debug(
      `[AttentiveFields] handleCanopusIDChange: brandId: ${brandId}, brandName: ${brandName}`
    );
    formik.setFieldValue('canopus_id', brandId);
    formik.setFieldValue('canopus_name', brandName);
  };

  const handlePartyIdChange = (partyId, partyName) => {
    logger.debug(
      `[AttentiveFields] handlePartyIdChange: partyId: ${partyId}, partyName: ${partyName}`
    );
    formik.setFieldValue('party_id', partyId);
    formik.setFieldValue('party_name', partyName);
  };

  // active/inactive toggle confirmation modal
  const toggle = (ok?) => {
    if (ok === false) {
      formik.setFieldValue('active', !formik.values['active']); // Maybe look at default here instead
    }
    setModal(!modal);
  };

  // this is called when trying to toggle active. It opens the confirmation modal and stores the desired active value
  const confirmSetActive = (value: boolean) => {
    toggle();
  };

  // this is called when the confirm modal button is clicked. It sets the active value and closes the modal.
  const setActiveConfirmed = () => {
    toggle();
  };

  const setActiveCancelled = () => {
    toggle(false);
  };

  const showTempActiveErrorMessage = (message: string) => {
    setActiveErrorMessage(message);
    setTimeout(() => {
      setActiveErrorMessage('');
    }, 5000);
  };

  const handleIsActiveChanged = async (e) => {
    setActiveErrorMessage('');
    const check = e.target.checked;
    logger.debug(
      `[handleIsActiveChanged] called - e.target.checked: ${e.target.checked} (${check}), for account_id: ${account_id}, model.key: ${model.key}`
    );

    // 1. does it have all necessary fields
    if (!model.key) {
      logger.debug(
        `[handleIsActiveChanged] cannot change to active as account not saved yet`
      );
      showTempActiveErrorMessage(
        'Cannot change account to active as account not saved yet.'
      );
      return;
    }

    // if ok to change then call:
    confirmSetActive(check);
  };

  const attentiveAccountLevels =
    (Object.values(enumerations.attentiveAccountLevels) as any) || [];
  const countriesValues = (Object.values(countries) as any) || [];
  const languages = (Object.values(enumerations.languages) as any) || [];
  const timezones = (Object.values(enumerations.timezones) as any) || [];
  const labels = (Object.values(labelValues) as any) || [];

  const disableIfInactive =
    formik.values['active'] === false || decommissioningCommenced
      ? true
      : false;
  const activeIsSaved = model.active === formik.values['active'];
  const isNewForm = !account_id;

  return (
    <React.Fragment>
      {errorMessage !== '' ? (
        <Row>
          <Col sm={12}>
            <Alert color="danger">{errorMessage}</Alert>
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col sm={6}>
          {decommissioningCommenced && (
            <Alert color="info" className="w-100 text-start">
              {decommissioningStatus === 'complete'
                ? 'Account Decommissioning Complete'
                : 'Account Decommissioning Requested'}
            </Alert>
          )}
        </Col>
        <Col sm={6} className="d-flex justify-content-end">
          {activeErrorMessage && (
            <Alert color="info" className="w-100 text-start">
              {activeErrorMessage}
            </Alert>
          )}
          <Can I="delete" an="account">
            <FormGroup switch>
              <Input
                type="switch"
                id="active"
                name="active"
                onChange={handleIsActiveChanged}
                checked={isNewForm ? false : formik.values['active']}
                disabled={decommissioningCommenced}
              />
              <Label for="active">
                Active {activeIsSaved ? '' : '(save account to take effect)'}
              </Label>
            </FormGroup>
          </Can>
        </Col>
      </Row>
      <Row className={accountStyles.formRow}>
        <Col sm="6">
          <Field
            name="account_name"
            label="Account Name"
            onChange={handleNameChange}
            required
            disabled={disableIfInactive}
          />
          {!account_id && (
            <Alert color="info">
              Account ID will be populated when an Attentive credential is added
              to DAM
            </Alert>
          )}
          <Field
            name="account_id"
            label="Attentive Account ID"
            disabled={true}
          />
          <SelectField
            name="account_level"
            label="Account Level"
            options={attentiveAccountLevels}
            isMulti={false}
            required
            isDisabled={disableIfInactive}
          />
          <BrandInputField
            name="canopus_id"
            label="Canopus ID"
            brandFieldName="canopus_name"
            brandFieldNameLabel="Brand Name"
            required={false}
            disabled={true}
            handleOnChange={handleCanopusIDChange}
            brandService={BrandService.CANOPUS}
          />
          <BrandInputField
            name="party_id"
            label="Party ID"
            brandFieldName="party_name"
            brandFieldNameLabel="Party Name"
            required={true}
            disabled={disableIfInactive}
            handleOnChange={handlePartyIdChange}
            brandService={BrandService.PARTY_SERVICE}
          />
          <SelectField
            name="label"
            label="Label"
            required
            options={labels}
            isMulti={false}
            isDisabled={disableIfInactive}
          />
          <SelectField
            name="country_code"
            label="Territory Operating Country"
            options={countriesValues}
            isMulti={false}
            required
            isDisabled={disableIfInactive}
          />
          <SelectField
            name="language_code"
            label="Language"
            options={languages}
            isMulti={false}
            required
            isDisabled={disableIfInactive}
          />
          <SelectField
            name="timezone"
            label="Timezone"
            options={timezones}
            isMulti={false}
            required
            isDisabled={disableIfInactive}
          />
        </Col>
      </Row>
      <Modal isOpen={modal} toggle={(e) => toggle(false)}>
        <ModalHeader toggle={(e) => toggle(false)}>Please Confirm</ModalHeader>
        <ModalBody>
          Please confirm that you would like to change the 'active' status of
          this account. Once saved this change will also update all of the Vault
          credentials' active flag for this account.
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={(e) => setActiveConfirmed()}>
            Confirm
          </Button>{' '}
          <Button color="secondary" onClick={(e) => setActiveCancelled()}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};
